import { useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { Project } from 'api/project';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import {
  ALL_PROJECTS_QUERY_KEY,
  PROJECTS_META_QUERY_KEY,
} from 'queries/constants';
import { useAutoAllocationStateManager } from '../../auto-allocation-state-manager';
import { useRefreshAllocationsManager } from '../../refresh-allocations-manager';
import { useMultiProjectWsUpdatesInRM } from './multi-project-ws-manager';
import { ProjectMembersWsProps } from './project-members-socket-manager';

export const useProjectUpdateWsManager = (
  props: ProjectMembersWsProps,
): void => {
  const queryClient = useQueryClient();
  const {
    projectChannels,
    projectLookup,
    groupBy,
    schedulerRef,
    teamMembership,
  } = props;
  const { enableAutoAllocateForProject, disableAutoAllocateForProject } =
    useAutoAllocationStateManager({
      groupBy,
      schedulerRef,
    });
  const { refreshAllocationForProject } = useRefreshAllocationsManager(props);

  const refreshAllocation = useCallback(
    (projectId) => {
      const oldProjectObject = projectLookup[projectId];

      refreshAllocationForProject(oldProjectObject, teamMembership[projectId]);
    },
    [projectLookup, refreshAllocationForProject, teamMembership],
  );
  const refreshAllocationProxy = useRef<(projectId: number) => void>();

  refreshAllocationProxy.current = refreshAllocation;
  const getDebouncedRefreshAllocation = useCallback(() => {
    const timers = {};

    return (projectId) => {
      if (timers[projectId] != null) {
        clearTimeout(timers[projectId]);
        timers[projectId] = null;
      }
      timers[projectId] = setTimeout(() => {
        refreshAllocationProxy.current?.(projectId);
        clearTimeout(timers[projectId]);
        timers[projectId] = null;
      }, 500);
    };
  }, []);
  const debouncedRefreshAllocation = useMemo(
    () => getDebouncedRefreshAllocation(),
    [getDebouncedRefreshAllocation],
  );

  const handleUpdate = useCallback(
    (socketData: SocketData<Project>) => {
      const {
        data: { projectId, autoAllocation },
      } = socketData;
      const oldProjectObject = projectLookup[projectId];

      if (oldProjectObject.autoAllocation !== autoAllocation) {
        if (autoAllocation === true) {
          enableAutoAllocateForProject(projectId);
          debouncedRefreshAllocation(projectId);
        } else {
          disableAutoAllocateForProject(projectId);
        }
      }
      queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
      queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
    },
    [
      projectLookup,
      queryClient,
      enableAutoAllocateForProject,
      debouncedRefreshAllocation,
      disableAutoAllocateForProject,
    ],
  );
  const handleRefresh = useCallback(
    (socketData: SocketData<Project>) => {
      const {
        data: { projectId },
      } = socketData;

      debouncedRefreshAllocation(projectId);
    },
    [debouncedRefreshAllocation],
  );

  useMultiProjectWsUpdatesInRM(
    projectChannels,
    handleUpdate,
    EventType(Action.Update, ObjectType.Project),
  );
  useMultiProjectWsUpdatesInRM(
    projectChannels,
    handleRefresh,
    EventType(Action.Refresh, ObjectType.Project),
  );
};
